import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import { PipelineFunnel } from "@/components/PipelineFunnel";
import { formatINRShort, formatDate } from "@/lib/constants";
import { ChartCard, COLORS, cr2, r2 } from "./MISOverview";

const tooltipStyle = { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" };
const DONUT = ["#a3231b", "#0ea5e9", "#f59e0b", "#10b981", "#64748b"];

function CoverageRow({ label, plan, actual, unit, testId }) {
  const pctv = plan ? Math.round((actual / plan) * 100) : 0;
  const w = Math.min(100, plan ? (actual / plan) * 100 : 0);
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="font-mono text-slate-500">
          {r2(actual)} / {r2(plan)} {unit} · <b className={pctv >= 100 ? "text-emerald-600" : pctv >= 60 ? "text-amber-600" : "text-red-600"}>{pctv}%</b>
        </span>
      </div>
      <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all"
          style={{ width: `${w}%`, background: pctv >= 100 ? "#10b981" : pctv >= 60 ? "#f59e0b" : "#dc2626" }} />
      </div>
    </div>
  );
}

const RATING_STYLE = {
  Hot: "bg-red-50 text-red-700",
  Warm: "bg-amber-50 text-amber-700",
  Cold: "bg-sky-50 text-sky-700",
};

function RatingChips({ rows, testId }) {
  const counts = rows.reduce((a, r) => {
    if (RATING_STYLE[r.rating]) a[r.rating] = (a[r.rating] || 0) + r.count;
    return a;
  }, {});
  return (
    <div className="flex gap-1.5 flex-wrap justify-end" data-testid={testId}>
      {["Hot", "Warm", "Cold"].map((k) => (
        <span key={k} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${RATING_STYLE[k]}`}>
          {k} {counts[k] || 0}
        </span>
      ))}
    </div>
  );
}

function RatingBadge({ value }) {
  if (!value || value === "None") return <span className="text-slate-300">—</span>;
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${RATING_STYLE[value] || "bg-slate-100 text-slate-600"}`}>{value}</span>;
}

const DRILL_TITLES = { universe: "Universe accounts", leads: "Leads", opportunities: "Opportunities" };

function FunnelDrillDialog({ stage, zone, onClose }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["funnel-records", stage, zone],
    queryFn: async () => (await api.get("/sales-mis/funnel-records", { params: { stage, zone } })).data,
    enabled: !!stage,
  });
  const rows = data?.rows || [];
  const open = (r) => {
    if (!r.id) return;
    if (stage === "leads") navigate(`/leads/${r.id}`);
    if (stage === "opportunities") navigate(`/deals/${r.id}`);
  };
  return (
    <Dialog open={!!stage} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="funnel-drill-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {DRILL_TITLES[stage] || ""}
            <span className="text-sm font-normal text-slate-400">
              {zone !== "All" ? `${zone} · ` : ""}{data ? `${rows.length}${data.total > rows.length ? ` of ${data.total}` : ""} records` : ""}
            </span>
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No records match this stage{zone !== "All" ? ` in ${zone}` : ""}.</p>
        ) : (
          <>
          {stage !== "universe" && <p className="text-[11px] text-slate-400 -mt-1">Click a row to open the {stage === "leads" ? "lead" : "opportunity"} for instant action.</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  {stage === "universe" && <><th className="py-1.5 pr-3">Company</th><th className="pr-3">Location</th><th className="pr-3">Category</th><th className="pr-3">Region</th><th>Status</th></>}
                  {stage === "leads" && <><th className="py-1.5 pr-3">Lead</th><th className="pr-3">Company</th><th className="pr-3">Zone</th><th className="pr-3">Rating</th><th className="pr-3">Status</th><th className="pr-3 text-right">Value</th><th>Owner</th></>}
                  {stage === "opportunities" && <><th className="py-1.5 pr-3">Opportunity</th><th className="pr-3">Company</th><th className="pr-3">Stage</th><th className="pr-3">Rating</th><th className="pr-3 text-right">Value</th><th className="pr-3">EDC</th><th>Owner</th></>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}
                    className={`border-b border-slate-50 ${stage === "universe" ? "hover:bg-slate-50/60" : "hover:bg-orange-50/40 cursor-pointer"}`}
                    onClick={() => open(r)} data-testid={`funnel-drill-row-${i}`}>
                    {stage === "universe" && <>
                      <td className="py-1.5 pr-3 font-medium text-slate-800">{r.company}</td>
                      <td className="pr-3">{r.location || "—"}</td><td className="pr-3">{r.category || "—"}</td>
                      <td className="pr-3">{r.region || "—"}</td><td className="text-slate-500">{r.status || "—"}</td>
                    </>}
                    {stage === "leads" && <>
                      <td className="py-1.5 pr-3 font-medium text-slate-800">{r.name}</td>
                      <td className="pr-3">{r.company || "—"}</td>
                      <td className="pr-3">{r.zone || r.region || "—"}</td>
                      <td className="pr-3"><RatingBadge value={r.rating} /></td>
                      <td className="pr-3 text-slate-500">{r.lead_status || "—"}</td>
                      <td className="pr-3 text-right font-mono">{r.expected_value ? formatINRShort(r.expected_value) : "—"}</td>
                      <td className="text-slate-500">{r.owner_name || "—"}</td>
                    </>}
                    {stage === "opportunities" && <>
                      <td className="py-1.5 pr-3 font-medium text-slate-800">{r.name}</td>
                      <td className="pr-3">{r.company || "—"}</td>
                      <td className="pr-3 text-slate-500">{r.stage || "—"}</td>
                      <td className="pr-3"><RatingBadge value={r.sub_rating} /></td>
                      <td className="pr-3 text-right font-mono">{r.deal_value ? formatINRShort(r.deal_value) : "—"}</td>
                      <td className="pr-3 whitespace-nowrap">{r.expected_close_date ? formatDate(r.expected_close_date) : "—"}</td>
                      <td className="text-slate-500">{r.owner_name || "—"}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UniverseFunnel({ funnel, zone }) {
  const [drill, setDrill] = useState(null);
  const total = (rows) => rows.reduce((a, r) => a + r.count, 0);
  const uni = total(funnel.universe);
  const leads = total(funnel.leads);
  const opps = total(funnel.opportunities);
  const oppValue = funnel.opportunities.reduce((a, r) => a + (r.value || 0), 0);
  const max = Math.max(uni, leads, opps, 1);
  const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
  const stages = [
    { key: "universe", label: "Universe accounts", count: uni, color: "#334155" },
    { key: "leads", label: "Leads", count: leads, color: "#0ea5e9", chips: funnel.leads, chipId: "mis-funnel-lead-ratings" },
    { key: "opportunities", label: "Opportunities", count: opps, color: "#a3231b", chips: funnel.opportunities, chipId: "mis-funnel-opp-ratings", sub: formatINRShort(oppValue) },
  ];
  return (
    <Card className="p-4 mb-5" data-testid="mis-universe-funnel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-heading font-semibold text-sm text-slate-800">CRM funnel — Universe → Leads → Opportunities</h3>
        <p className="text-[11px] text-slate-400">
          Universe → Lead conversion <b className="text-slate-600">{pct(leads, uni)}</b> · Lead → Opportunity <b className="text-slate-600">{pct(opps, leads)}</b>
        </p>
      </div>
      <div className="space-y-3 mt-4">
        {stages.map((s) => (
          <div key={s.label} className="flex items-center gap-3 group cursor-pointer rounded-md -mx-1.5 px-1.5 py-0.5 hover:bg-slate-50 transition-colors"
            onClick={() => setDrill(s.key)} data-testid={`mis-funnel-stage-${s.key}`}>
            <span className="w-36 sm:w-40 text-xs font-medium text-slate-600 group-hover:text-slate-900 shrink-0">{s.label}</span>
            <div className="flex-1 h-6 rounded bg-slate-50 overflow-hidden">
              <div className="h-full rounded flex items-center px-2 transition-all"
                style={{ width: `${Math.max(7, (s.count / max) * 100)}%`, background: s.color }}>
                <span className="text-[11px] font-bold text-white">{s.count}</span>
              </div>
            </div>
            <div className="w-40 sm:w-48 shrink-0 flex items-center justify-end gap-2">
              {s.sub && <span className="text-[10px] font-mono text-slate-400 whitespace-nowrap">{s.sub}</span>}
              {s.chips ? <RatingChips rows={s.chips} testId={s.chipId} /> : <span className="text-[10px] text-slate-300">all accounts</span>}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-300 mt-2">Click a stage to see the records behind it.</p>
      <FunnelDrillDialog stage={drill} zone={zone} onClose={() => setDrill(null)} />
    </Card>
  );
}

export function MISPipelineSection({ dash, deals, crm, funnel, zone, product }) {
  const sum = (fn) => dash.reduce((a, r) => a + fn(r), 0);
  const plan = sum((r) => r.sales_plan);

  const dealsByStage = crm.reduce((acc, p) => {
    if (!p.stage) return acc;
    acc[p.stage] = acc[p.stage] || { count: 0, value: 0 };
    acc[p.stage].count += p.count;
    acc[p.stage].value += p.value;
    return acc;
  }, {});

  const byPerson = Object.entries(deals.reduce((acc, d) => {
    const k = d.sales_member || "—";
    acc[k] = (acc[k] || 0) + d.value_lakhs;
    return acc;
  }, {})).map(([name, v]) => ({ name, value: r2(v / 100) })).sort((a, b) => b.value - a.value).slice(0, 10);

  const kindSplit = ["Opportunity", "Lead"].map((k) => ({
    name: k, value: r2(deals.filter((d) => d.kind === k).reduce((a, d) => a + d.value_lakhs, 0) / 100),
  }));
  const univicSplit = [
    { name: "UniVic specified", value: deals.filter((d) => d.univic === "Y").length },
    { name: "Not specified", value: deals.filter((d) => d.univic === "N").length },
  ];
  const buckets = [
    { b: "> 0.25 Cr", Opportunities: sum((r) => r.opp_gt25), Leads: sum((r) => r.leads_gt25) },
    { b: "> 0.50 Cr", Opportunities: sum((r) => r.opp_gt50), Leads: sum((r) => r.leads_gt50) },
    { b: "> 1.00 Cr", Opportunities: sum((r) => r.opp_gt100), Leads: sum((r) => r.leads_gt100) },
  ];
  const topDeals = [...deals].sort((a, b) => b.value_lakhs - a.value_lakhs).slice(0, 10);

  return (
    <>
      {funnel && <UniverseFunnel funnel={funnel} zone={zone || "All"} />}
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <Card className="p-4" data-testid="mis-coverage-funnel">
          <h3 className="font-heading font-semibold text-sm text-slate-800">MIS funnel coverage (INR Cr)</h3>
          <p className="text-[11px] text-slate-400 mb-4">Leads target 6× plan · Opportunities 3× plan · Booking & Billing vs annual plan</p>
          <div className="space-y-4">
            <CoverageRow label="Leads generated" plan={sum((r) => r.leads_plan)} actual={sum((r) => r.leads_act)} unit="" testId="mis-cov-leads" />
            <CoverageRow label="Opportunities" plan={sum((r) => r.opp_plan)} actual={sum((r) => r.opp_act)} unit="" testId="mis-cov-opps" />
            <CoverageRow label="YTD Booking" plan={plan} actual={sum((r) => r.ytd_booking)} unit="" testId="mis-cov-booking" />
            <CoverageRow label="YTD Billing" plan={plan} actual={sum((r) => r.ytd_billing)} unit="" testId="mis-cov-billing" />
          </div>
        </Card>
        <Card className="p-4" data-testid="mis-crm-funnel">
          <h3 className="font-heading font-semibold text-sm text-slate-800">CRM pipeline funnel</h3>
          <p className="text-[11px] text-slate-400 mb-2">Live opportunities in the CRM by stage{Object.keys(dealsByStage).length === 0 ? "" : ""}</p>
          <PipelineFunnel dealsByStage={dealsByStage} />
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <ChartCard title="Large-deal pipeline by salesperson (INR Cr)" testId="mis-chart-person" height={260}>
          <ResponsiveContainer>
            <BarChart data={byPerson} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={82} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => cr2(v)} />
              <Bar dataKey="value" fill="#a3231b" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Lead vs Opportunity value" testId="mis-chart-kind" height={260}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={kindSplit} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                {kindSplit.map((_, i) => <Cell key={i} fill={DONUT[i]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => cr2(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Value buckets (count)" subtitle="Leads & opportunities above thresholds" testId="mis-chart-buckets" height={260}>
          <ResponsiveContainer>
            <BarChart data={buckets} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="b" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Leads" fill={COLORS.booking} radius={[3, 3, 0, 0]} />
              <Bar dataKey="Opportunities" fill="#a3231b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Card className="p-4 mb-5" data-testid="mis-top-deals">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold text-sm text-slate-800">Top large deals</h3>
          <div className="flex gap-3 text-[11px] text-slate-400">
            <span>UniVic specified: <b className="text-emerald-600">{univicSplit[0].value}</b></span>
            <span>Not specified: <b className="text-amber-600">{univicSplit[1].value}</b></span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-1.5 pr-3">Project</th><th className="pr-3">Zone</th><th className="pr-3">Branch</th>
                <th className="pr-3">Owner</th><th className="pr-3">Type</th><th className="pr-3">Product</th>
                <th className="pr-3 text-right">Value (INR Cr)</th><th className="pr-3">EDC</th><th>Latest update</th>
              </tr>
            </thead>
            <tbody>
              {topDeals.map((d, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`mis-deal-row-${i}`}>
                  <td className="py-1.5 pr-3 font-medium text-slate-800 whitespace-nowrap">{d.project}</td>
                  <td className="pr-3">{d.zone}</td><td className="pr-3">{d.branch}</td>
                  <td className="pr-3 whitespace-nowrap">{d.sales_member}</td>
                  <td className="pr-3"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${d.kind === "Opportunity" ? "bg-red-50 text-red-700" : "bg-sky-50 text-sky-700"}`}>{d.kind}</span></td>
                  <td className="pr-3 whitespace-nowrap">{d.product}</td>
                  <td className="pr-3 text-right font-mono font-semibold">{(Number(d.value_lakhs || 0) / 100).toFixed(2)}</td>
                  <td className="pr-3 whitespace-nowrap">{d.edc}</td>
                  <td className="text-slate-400 max-w-[260px] truncate">{d.latest_remark}</td>
                </tr>
              ))}
              {topDeals.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-slate-400">No deals match the filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
